<script setup lang="ts">
/**
 * Группа прогонов — макет 20:61: шапка 44 со сводкой справа, строки 38.
 *
 * Шапка группы с упавшим прогоном красная: экран открывают ради упавшего, и
 * найти его должно быть можно, не читая строки. Свёрнутая группа шапку
 * оставляет — иначе список выглядит поредевшим, а не сложенным.
 */
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-vue-next'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import PipelineBadge from '@/features/issue/components/PipelineBadge.vue'
import { typeColor, typeGlyph } from '@/features/issues/visuals'

import type { PipelineGroup } from '../api'

const props = defineProps<{ group: PipelineGroup; open: boolean; stamp: (iso: string) => string }>()

const emit = defineEmits<{ toggle: [] }>()

const { t } = useI18n()

/** Сводка группы: «2 success · 1 running» — как в макете, статусами GitLab. */
const counts = computed(() =>
  Object.entries(props.group.counts)
    .sort((a, b) => b[1] - a[1])
    .map(([status, count]) => `${count} ${status}`)
    .join(' · '),
)
</script>

<template>
  <section class="pgroup">
    <button
      type="button"
      class="pgroup__head"
      :class="{ 'pgroup__head--failed': group.hasFailed }"
      :aria-expanded="open"
      @click="emit('toggle')"
    >
      <ChevronDown v-if="open" :size="14" />
      <ChevronRight v-else :size="14" />
      <span
        v-if="group.epicKey"
        class="pgroup__glyph"
        :style="{ color: typeColor('ISSUE_TYPE_EPIC') }"
        aria-hidden="true"
      >
        {{ typeGlyph('ISSUE_TYPE_EPIC') }}
      </span>
      <span v-if="group.epicKey" class="pgroup__key">{{ group.epicKey }}</span>
      <span class="pgroup__title">{{ group.title }}</span>
      <span class="pgroup__spacer" />
      <span class="pgroup__counts">{{ counts }}</span>
    </button>

    <div v-if="open" class="pgroup__rows">
      <div v-for="row in group.rows" :key="row.run.id" class="prow">
        <RouterLink v-if="row.issueKey" class="prow__issue" :to="`/issue/${row.issueKey}`">
          {{ row.issueKey }}
        </RouterLink>
        <span v-else class="prow__issue prow__issue--none">—</span>
        <span class="prow__service">{{ row.run.projectPath.split('/').pop() }}</span>
        <span class="prow__ref">{{ row.run.ref }}</span>
        <span class="prow__spacer" />
        <span class="prow__sha">{{ row.run.sha.slice(0, 7) }}</span>
        <span class="prow__at">{{ stamp(row.run.createdAt) }}</span>
        <PipelineBadge
          :pipeline="{
            id: row.run.pipelineId,
            url: row.run.url,
            status: row.run.status,
            durationSeconds: row.run.durationSeconds,
            projectPath: row.run.projectPath,
            ref: row.run.ref,
          }"
        />
        <a
          class="prow__link"
          :href="row.run.url"
          target="_blank"
          rel="noopener"
          :aria-label="t('devops.pipelines.open')"
          :title="t('devops.pipelines.open')"
        >
          <ExternalLink :size="14" />
        </a>
      </div>
    </div>
  </section>
</template>

<style scoped>
.pgroup {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  overflow: hidden;
}

.pgroup__head {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 44px;
  padding: 0 16px;
  border: none;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-bg-muted);
  color: var(--color-text-muted);
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.pgroup__head--failed {
  background: var(--color-danger-tint);
}

.pgroup__glyph {
  font-size: var(--font-size-body-sm);
  line-height: 1;
}

.pgroup__key {
  color: var(--color-text-primary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
}

.pgroup__title {
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.pgroup__spacer {
  flex: 1;
  min-width: 0;
}

.pgroup__counts {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
}

.prow {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 38px;
  padding: 0 16px;
  border-bottom: 1px solid var(--color-border);
}

.prow:last-child {
  border-bottom: none;
}

.prow__issue {
  width: 76px;
  color: var(--color-primary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  text-decoration: none;
}

.prow__issue--none {
  color: var(--color-text-muted);
}

.prow__service {
  width: 130px;
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
}

.prow__ref,
.prow__sha,
.prow__at {
  overflow: hidden;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.prow__ref {
  max-width: 260px;
}

.prow__spacer {
  flex: 1;
  min-width: 0;
}

.prow__link {
  display: inline-flex;
  color: var(--color-text-muted);
}

.prow__link:hover {
  color: var(--color-text-primary);
}
</style>
